<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useTasksWidget } from '@/composables/useTasksWidget'
import { useTasksStore } from '@/views/Tasks/store'
import { useTaskBoardsStore } from '@/views/Tasks/store/boards'
import { useAuthStore } from '@/stores/auth'
import { formatDate } from '@/utils/formatters'
import type { Task, Priority } from '@/types'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import type { Component } from 'vue'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  CheckSquare,
  Minus,
  X,
  AlertTriangle,
  Clock,
  ArrowRight,
  Inbox,
  ExternalLink,
  Calendar,
  Flame,
  CircleDot,
  CheckCircle2,
  LayoutGrid,
  Search,
} from 'lucide-vue-next'

const { isOpen, minimize, close } = useTasksWidget()
const store = useTasksStore()
const boardsStore = useTaskBoardsStore()
const authStore = useAuthStore()
const router = useRouter()
const searchQuery = ref('')

const priorityConfig: Record<Priority, { label: string; color: string }> = {
  critical: { label: 'Critico', color: 'text-red-500 bg-red-500/10' },
  high: { label: 'Alto', color: 'text-orange-500 bg-orange-500/10' },
  medium: { label: 'Medio', color: 'text-blue-500 bg-blue-500/10' },
  low: { label: 'Bajo', color: 'text-muted-foreground bg-muted' },
}

const now = new Date()

// Filter from global store: only tasks assigned to current user + search
const myTasks = computed(() => {
  let tasks = store.tasks.filter((t) => t.assignedTo?.includes(authStore.userId || ''))
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    tasks = tasks.filter((t) =>
      t.title.toLowerCase().includes(q) ||
      (t.taskNumber && `#${t.taskNumber}`.includes(q)) ||
      t.description?.toLowerCase().includes(q)
    )
  }
  return tasks
})

const overdueTasks = computed(() =>
  myTasks.value.filter((t) => t.dueDate && t.dueDate < now && t.status !== 'done' && t.status !== 'cancelled')
)

const criticalTasks = computed(() =>
  myTasks.value.filter((t) => t.priority === 'critical' && t.status !== 'done' && t.status !== 'cancelled')
)

const highTasks = computed(() =>
  myTasks.value.filter((t) => t.priority === 'high' && t.status !== 'done' && t.status !== 'cancelled')
)

const mediumTasks = computed(() =>
  myTasks.value.filter((t) => t.priority === 'medium' && t.status !== 'done' && t.status !== 'cancelled')
)

const lowTasks = computed(() =>
  myTasks.value.filter((t) => t.priority === 'low' && t.status !== 'done' && t.status !== 'cancelled')
)

const activeTasks = computed(() =>
  myTasks.value.filter((t) => t.status !== 'done' && t.status !== 'cancelled')
)

const recentTasks = computed(() =>
  myTasks.value.slice(0, 15)
)

const getBoardName = (boardId?: string) => {
  if (!boardId) return null
  return boardsStore.boardById(boardId)?.name || null
}

const navigateToTask = (task: Task) => {
  close()
  router.push(`/tasks/${task.id}`)
}

const navigateToBoard = (boardId: string) => {
  close()
  router.push(`/tasks/board/${boardId}`)
}

const navigateToBoards = () => {
  close()
  router.push('/tasks')
}

const isOverdue = (task: Task) =>
  task.dueDate && task.dueDate < now && task.status !== 'done' && task.status !== 'cancelled'
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="opacity-0 translate-x-full"
      enter-to-class="opacity-100 translate-x-0"
      leave-from-class="opacity-100 translate-x-0"
      leave-to-class="opacity-0 translate-x-full"
    >
      <div
        v-if="isOpen"
        class="fixed top-0 right-0 bottom-0 z-[100] w-full sm:w-[420px] bg-background border-l shadow-2xl flex flex-col"
      >
        <!-- Header -->
        <div class="h-14 border-b px-4 flex items-center justify-between shrink-0">
          <div class="flex items-center gap-2">
            <CheckSquare class="h-5 w-5 text-primary" />
            <h2 class="font-semibold">Mis Tareas</h2>
            <Badge v-if="overdueTasks.length > 0" variant="destructive" class="text-[10px] px-1.5 py-0">
              {{ overdueTasks.length }} vencidas
            </Badge>
          </div>
          <div class="flex items-center gap-1">
            <Button variant="ghost" size="icon" class="h-8 w-8" @click="navigateToBoards" title="Ir a tableros">
              <ExternalLink class="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" class="h-8 w-8" @click="minimize" title="Minimizar">
              <Minus class="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" class="h-8 w-8" @click="close" title="Cerrar">
              <X class="h-4 w-4" />
            </Button>
          </div>
        </div>

        <!-- Stats bar -->
        <div class="px-4 py-3 border-b bg-muted/30 shrink-0">
          <div class="grid grid-cols-5 gap-2 text-center">
            <div>
              <div class="text-lg font-bold text-red-600">{{ overdueTasks.length }}</div>
              <div class="text-[10px] text-muted-foreground">Vencidas</div>
            </div>
            <div>
              <div class="text-lg font-bold text-red-500">{{ criticalTasks.length }}</div>
              <div class="text-[10px] text-muted-foreground">Critico</div>
            </div>
            <div>
              <div class="text-lg font-bold text-orange-500">{{ highTasks.length }}</div>
              <div class="text-[10px] text-muted-foreground">Alto</div>
            </div>
            <div>
              <div class="text-lg font-bold text-blue-500">{{ mediumTasks.length }}</div>
              <div class="text-[10px] text-muted-foreground">Medio</div>
            </div>
            <div>
              <div class="text-lg font-bold text-muted-foreground">{{ lowTasks.length }}</div>
              <div class="text-[10px] text-muted-foreground">Bajo</div>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-y-auto">
          <!-- Loading -->
          <div v-if="store.isLoading" class="p-4 space-y-3">
            <Skeleton v-for="i in 5" :key="i" class="h-16 w-full" />
          </div>

          <!-- Empty -->
          <div v-else-if="myTasks.length === 0" class="flex flex-col items-center justify-center h-full px-4">
            <Inbox class="h-10 w-10 text-muted-foreground mb-3" />
            <p class="text-sm text-muted-foreground mb-4">No tienes tareas asignadas</p>
            <Button variant="outline" size="sm" @click="navigateToBoards">
              <LayoutGrid class="mr-2 h-3.5 w-3.5" />
              Ver tableros
            </Button>
          </div>

          <!-- Task tabs -->
          <Tabs v-else default-value="tasks" class="flex flex-col h-full">
            <div class="px-4 pt-3 space-y-2 shrink-0">
              <TabsList class="w-full">
                <TabsTrigger value="tasks" class="flex-1 text-xs gap-1">
                  <Flame class="h-3 w-3" />
                  Mis tareas
                  <Badge v-if="activeTasks.length > 0" variant="secondary" class="text-[9px] px-1 py-0">
                    {{ activeTasks.length }}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="boards" class="flex-1 text-xs gap-1">
                  <LayoutGrid class="h-3 w-3" />
                  Tableros
                </TabsTrigger>
              </TabsList>
              <div class="relative">
                <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  v-model="searchQuery"
                  placeholder="Buscar tarea..."
                  class="h-8 pl-8 text-xs"
                />
              </div>
            </div>

            <!-- My tasks -->
            <TabsContent value="tasks" class="flex-1 overflow-y-auto px-4 pb-4 mt-0">
              <!-- Overdue section -->
              <div v-if="overdueTasks.length > 0" class="mt-3">
                <div class="flex items-center gap-1.5 mb-2">
                  <AlertTriangle class="h-3.5 w-3.5 text-red-500" />
                  <span class="text-xs font-semibold text-red-500 uppercase tracking-wider">Vencidas</span>
                </div>
                <div class="space-y-1.5">
                  <button
                    v-for="task in overdueTasks"
                    :key="task.id"
                    class="w-full flex items-start gap-3 p-2.5 rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition-colors text-left"
                    @click="navigateToTask(task)"
                  >
                    <div class="h-6 w-6 rounded flex items-center justify-center shrink-0 mt-0.5" :class="priorityConfig[task.priority]?.color">
                      <AlertTriangle class="h-3 w-3" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium truncate">{{ task.title }}</p>
                      <div class="flex items-center gap-2 mt-0.5">
                        <span class="text-[11px] text-red-500">{{ formatDate(task.dueDate!) }}</span>
                        <span v-if="task.taskNumber" class="text-[10px] text-muted-foreground">#{{ task.taskNumber }}</span>
                        <span v-if="getBoardName(task.boardId)" class="text-[10px] text-muted-foreground">{{ getBoardName(task.boardId) }}</span>
                      </div>
                    </div>
                    <ArrowRight class="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />
                  </button>
                </div>
              </div>

              <!-- All active tasks grouped by priority -->
              <div v-for="group in [
                { tasks: criticalTasks, label: 'Critico', icon: Flame, color: 'text-red-500' },
                { tasks: highTasks, label: 'Alto', icon: AlertTriangle, color: 'text-orange-500' },
                { tasks: mediumTasks, label: 'Medio', icon: CircleDot, color: 'text-blue-500' },
                { tasks: lowTasks, label: 'Bajo', icon: Clock, color: 'text-muted-foreground' },
              ]" :key="group.label">
                <div v-if="group.tasks.length > 0" class="mt-3">
                  <div class="flex items-center gap-1.5 mb-2">
                    <component :is="group.icon" class="h-3.5 w-3.5" :class="group.color" />
                    <span class="text-xs font-semibold uppercase tracking-wider" :class="group.color">{{ group.label }}</span>
                    <span class="text-[10px] text-muted-foreground">({{ group.tasks.length }})</span>
                  </div>
                  <div class="space-y-1.5">
                    <button
                      v-for="task in group.tasks"
                      :key="task.id"
                      class="w-full flex items-start gap-3 p-2.5 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                      @click="navigateToTask(task)"
                    >
                      <div class="h-6 w-6 rounded flex items-center justify-center shrink-0 mt-0.5" :class="priorityConfig[task.priority]?.color">
                        <component :is="group.icon" class="h-3 w-3" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm truncate">{{ task.title }}</p>
                        <div class="flex items-center gap-2 mt-0.5">
                          <span v-if="task.taskNumber" class="text-[10px] text-muted-foreground">#{{ task.taskNumber }}</span>
                          <span v-if="getBoardName(task.boardId)" class="text-[10px] text-muted-foreground">{{ getBoardName(task.boardId) }}</span>
                          <span v-if="task.dueDate" class="text-[10px] flex items-center gap-0.5" :class="isOverdue(task) ? 'text-red-500' : 'text-muted-foreground'">
                            <Calendar class="h-2.5 w-2.5" />
                            {{ formatDate(task.dueDate) }}
                          </span>
                        </div>
                      </div>
                      <ArrowRight class="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- No tasks -->
              <div v-if="activeTasks.length === 0" class="flex flex-col items-center justify-center py-12">
                <CheckCircle2 class="h-10 w-10 text-emerald-500 mb-3" />
                <p class="text-sm font-medium">Todo al día</p>
                <p class="text-xs text-muted-foreground">No tienes tareas activas</p>
              </div>
            </TabsContent>

            <!-- Boards -->
            <TabsContent value="boards" class="flex-1 overflow-y-auto px-4 pb-4 mt-0">
              <div v-if="boardsStore.boards.length > 0" class="space-y-2 mt-3">
                <button
                  v-for="board in boardsStore.boards"
                  :key="board.id"
                  class="w-full flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors text-left"
                  @click="navigateToBoard(board.id)"
                >
                  <div class="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <LayoutGrid class="h-4 w-4 text-primary" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium truncate">{{ board.name }}</p>
                    <div class="flex items-center gap-2 mt-0.5">
                      <span class="text-[11px] text-muted-foreground">{{ board.lists?.length || 0 }} listas</span>
                      <span class="text-[11px] text-muted-foreground">&middot;</span>
                      <span class="text-[11px] text-muted-foreground">{{ board.members?.length || 0 }} miembros</span>
                    </div>
                  </div>
                  <ArrowRight class="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-2" />
                </button>
              </div>
              <div v-else class="flex flex-col items-center justify-center py-12">
                <LayoutGrid class="h-10 w-10 text-muted-foreground mb-3" />
                <p class="text-xs text-muted-foreground">Sin tableros disponibles</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
